<!-- Previous calendar month: daily bookings for one selected location. -->
<template>
    <section class="last-month-report">
        <header class="last-month-header">
            <h2>Last month overview</h2>
            <p class="last-month-subtitle">
                <template v-if="applicable">
                    {{ periodLabel }} · daily bookings for the selected location.
                    Follows the location filter above, but not the date or experience filters.
                </template>
                <template v-else>
                    Select one location in the filter above to see last month per location.
                </template>
            </p>
        </header>

        <template v-if="applicable">
            <p v-if="loading" class="last-month-status">Loading last month data…</p>
            <p v-else-if="error" class="last-month-status last-month-status--error">{{ error }}</p>

            <template v-else-if="ready">
                <section class="summary-grid">
                    <article class="cijfer">
                        <span class="label">Total bookings</span>
                        <span class="waarde">{{ summary.yourTotal }}</span>
                        <span class="summary-sub">{{ summary.yourAvgPerDay }} / day avg</span>
                    </article>
                    <article class="cijfer">
                        <span class="label">Avg rating</span>
                        <span class="waarde">{{ formatRating(summary.avgRating) }}</span>
                    </article>
                    <article class="cijfer">
                        <span class="label">Avg location rating</span>
                        <span class="waarde">{{ formatRating(summary.avgLocationRating) }}</span>
                    </article>
                </section>

                <dashboard-panel
                    title="Bookings per day"
                    :subtitle="periodLabel"
                >
                    <dashboard-bar-chart-host :labels="labels" :datasets="datasets" />
                </dashboard-panel>
            </template>
        </template>
    </section>
</template>

<script setup>
import DashboardBarChartHost from "./DashboardBarChartHost.vue";
import DashboardPanel from "./DashboardPanel.vue";

defineProps({
    applicable: { type: Boolean, default: false },
    ready: { type: Boolean, default: false },
    loading: { type: Boolean, default: false },
    error: { type: String, default: "" },
    periodLabel: { type: String, default: "" },
    labels: { type: Array, default: () => [] },
    datasets: { type: Array, default: () => [] },
    summary: {
        type: Object,
        default: () => ({
            yourTotal: 0,
            yourAvgPerDay: 0,
            avgRating: 0,
            avgLocationRating: 0,
        }),
    },
});

function formatRating(value) {
    const rating = Number(value) || 0;
    return rating > 0 ? rating.toFixed(1) : "N/A";
}
</script>

<style scoped lang="scss">
.last-month-report {
    margin-top: var(--s-5);
    padding-top: var(--s-5);
    border-top: 1px solid var(--line);
}

.last-month-header {
    margin-bottom: var(--s-4);

    h2 {
        margin: 0 0 4px;
        font-family: var(--display-form);
        font-size: 18px;
        color: var(--text);
    }
}

.last-month-subtitle {
    margin: 0;
    color: var(--muted);
    font-size: 14px;
    max-width: 52rem;
}

.last-month-status {
    margin: 0.5rem 0 1rem;
    color: var(--muted);
    font-size: 14px;

    &--error {
        color: var(--rood);
    }
}

.summary-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(min(100%, 180px), 1fr));
    gap: var(--s-3);
    margin-bottom: var(--s-4);
}

/* Last-month-only footnote under the value; card chrome is shared `.cijfer`. */
.summary-sub {
    font-size: 12px;
    color: var(--muted);
}
</style>
